import { useState } from "react";
import type { Path, UseFormRegisterReturn } from "react-hook-form";
import { useSession } from "@core/session/SessionProvider";
import { Can } from "@core/permissions";
import { Icon } from "@core/icons";
import type { Promotion, PromotionInput } from "@core/types";
import { tokens } from "@core/theme/tokens";
import { tableSpacing } from "@core/table";
import { ImageWithPlaceholder } from "@core/media";
import {
  DialogActions,
  DialogHeader,
  EditorModal,
  type EditorModalTarget,
  FieldError,
  FileUploadChip,
  ImagePreviewBox,
  ImageUploadBox,
  ImpactConfirmDialog,
  InlineNote,
  inputStyle,
  LabeledField,
  LanguageSwitch,
  PortalButton,
  PreviewScrollHint,
  RequiredMark,
  resolveBilingualText as text,
  RowIconAction,
  selectStyle,
  Toggle,
  useEditorForm,
  useToast,
  type ContentLanguage,
} from "@portal/common";
import {
  applyApiFieldErrors,
  reportError,
  reportErrorWithPrefix,
  reportUnlessFieldError,
} from "@portal/common/errorMessage";
import { useOffers } from "../offers/offerQueries";
import { usePromotionMutations, usePromotions } from "./promotionQueries";
import {
  FIELDS_REQUIRED_MESSAGE,
  PROMOTION_FORM_FIELDS,
  promotionFormSchema,
  type PromotionFormValues,
} from "./promotionSchemas";
import { PromotionPreview } from "./PromotionPreview";

// SPEC-206 §10: mensaje literal que el cliente no puede validar antes de
// enviar (respaldo si el zod de cliente se saltea), mismo criterio que
// `SERVER_ERROR_MESSAGES` en offers/OffersScreen.tsx.
//
// C3 (code-reviewer, warn en la corrida de SPEC-206): `promotionFormSchema`
// no exige español cuando solo se llena el inglés de un campo bilingüe
// OPCIONAL (etiqueta/bajada/botón) — `buildInput` igual manda
// `{es: "", en: valor}`, y `promotion.service.ts:73-85` (`isBilingual`,
// `validateInput`) rechaza esa forma con `INVALID_REQUEST` porque `es` no es
// un string no vacío. Sin esta entrada, `handleError` no tenía código para
// ese caso y el guardado fallaba sin que la pantalla dijera nada.
const INVALID_REQUEST_MESSAGE =
  "El antetítulo, la bajada o el botón tienen inglés sin español; completa el español o borra el inglés";

const SERVER_ERROR_MESSAGES: Partial<Record<string, string>> = {
  FIELDS_REQUIRED: FIELDS_REQUIRED_MESSAGE,
  INVALID_REQUEST: INVALID_REQUEST_MESSAGE,
};

// SPEC-219 §4 (comentario original, offers/OffersScreen.tsx): fecha de
// calendario pura (`format: date`, sin hora ni zona) — nunca por
// `new Date(value)` + `toLocaleDateString`, que corre un día hacia atrás
// según el huso del navegador. Mismo defecto evitado acá.
function formatPromotionDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

// admin.jsx:276-280: "desde → hasta", con el sufijo "· indefinida" cuando no
// hay fecha de fin (SPEC-206 §3, ADR-019).
function formatValidity(promotion: Promotion): React.ReactNode {
  const from = formatPromotionDate(promotion.validFrom);
  const until = formatPromotionDate(promotion.validUntil);
  return (
    <>
      {from ?? "—"}{" "}
      {until ? (
        `→ ${until}`
      ) : (
        <span style={{ color: tokens.gold.light }}>· indefinida</span>
      )}
    </>
  );
}

function shortDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("es-EC", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function defaultsFor(promotion: Promotion | null): PromotionFormValues {
  if (!promotion) {
    return {
      overlineEs: "",
      overlineEn: "",
      titleEs: "",
      titleEn: "",
      subtitleEs: "",
      subtitleEn: "",
      ctaEs: "Ver la promoción",
      ctaEn: "",
      imageUrl: "",
      imageFileId: "",
      offerId: "",
      validFrom: "",
      validUntil: "",
      active: true,
    };
  }
  return {
    overlineEs: text(promotion.overline ?? null, "es"),
    overlineEn: text(promotion.overline ?? null, "en"),
    titleEs: text(promotion.title, "es"),
    titleEn: text(promotion.title, "en"),
    subtitleEs: text(promotion.subtitle ?? null, "es"),
    subtitleEn: text(promotion.subtitle ?? null, "en"),
    ctaEs: text(promotion.cta ?? null, "es"),
    ctaEn: text(promotion.cta ?? null, "en"),
    imageUrl: promotion.imageUrl,
    imageFileId: "",
    offerId: promotion.offer?.id ?? "",
    validFrom: promotion.validFrom ?? "",
    validUntil: promotion.validUntil ?? "",
    active: promotion.active,
  };
}

function buildInput(values: PromotionFormValues): PromotionInput {
  const hasOverline = values.overlineEs || values.overlineEn;
  const hasSubtitle = values.subtitleEs || values.subtitleEn;
  const hasCta = values.ctaEs || values.ctaEn;
  return {
    ...(hasOverline
      ? {
          overline: {
            es: values.overlineEs,
            ...(values.overlineEn ? { en: values.overlineEn } : {}),
          },
        }
      : {}),
    title: {
      es: values.titleEs,
      ...(values.titleEn ? { en: values.titleEn } : {}),
    },
    ...(hasSubtitle
      ? {
          subtitle: {
            es: values.subtitleEs,
            ...(values.subtitleEn ? { en: values.subtitleEn } : {}),
          },
        }
      : {}),
    ...(hasCta
      ? {
          cta: {
            es: values.ctaEs,
            ...(values.ctaEn ? { en: values.ctaEn } : {}),
          },
        }
      : {}),
    imageUrl: values.imageUrl,
    // PLAN-007 Fase 4d: viaja solo cuando el operador subió un archivo
    // nuevo en esta sesión del formulario; el backend le da prioridad
    // sobre `imageUrl` (PromotionInput.imageFileId).
    ...(values.imageFileId ? { imageFileId: values.imageFileId } : {}),
    offerId: values.offerId ? values.offerId : null,
    ...(values.validFrom ? { validFrom: values.validFrom } : {}),
    ...(values.validUntil ? { validUntil: values.validUntil } : {}),
    active: values.active,
  };
}

function previewUntilOf(values: PromotionFormValues): string | null {
  return formatPromotionDate(values.validUntil || null);
}

// PA-019-A (human-resolution-pa-019-a.json): conmutador de idioma POR CAMPO
// —uno junto a cada uno de los cuatro campos bilingües (antetítulo, título,
// bajada, llamado a la acción)—, a diferencia del conmutador único de
// cabecera que usan Bienvenida/Ofertas/Recompensas/Beneficios. Reutiliza
// `LanguageSwitch`/`RequiredMark`/`FieldError`, con estado propio por campo.
function BilingualField({
  fieldKey,
  label,
  required,
  registerEs,
  registerEn,
  error,
  multiline,
  placeholderEs,
}: {
  fieldKey: string;
  label: string;
  required?: boolean;
  registerEs: UseFormRegisterReturn;
  registerEn: UseFormRegisterReturn;
  error?: string | undefined;
  multiline?: boolean;
  placeholderEs?: string;
}) {
  const [lang, setLang] = useState<ContentLanguage>("es");
  const field = lang === "es" ? registerEs : registerEn;
  const placeholder =
    lang === "en" ? "Sin traducir · se mostrará en español" : placeholderEs;
  return (
    <div style={{ marginBottom: 16 }} data-testid={`bilingual-${fieldKey}`}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 7,
        }}
      >
        <span className="label">
          {label}
          {required ? <RequiredMark /> : null}
        </span>
        <LanguageSwitch value={lang} onChange={setLang} />
      </div>
      {multiline ? (
        <textarea
          {...field}
          aria-label={label}
          placeholder={placeholder}
          style={{ ...inputStyle, minHeight: 70, resize: "vertical" }}
        />
      ) : (
        <input
          {...field}
          aria-label={label}
          placeholder={placeholder}
          style={inputStyle}
        />
      )}
      {lang === "es" && error ? <FieldError message={error} /> : null}
    </div>
  );
}

interface PromotionEditorProps {
  promotion: Promotion | null;
  canWrite: boolean;
  submitting: boolean;
  // `applyFieldErrors` vuelca al formulario los rechazos por campo de la API
  // (p. ej. `validUntil` anterior a `validFrom`); `true` si asoció alguno.
  onSubmit: (
    input: PromotionInput,
    applyFieldErrors: (error: unknown) => boolean,
  ) => void;
  onCancel: () => void;
  // Revisión en vivo 2026-09-22, puntos #3/#4A/#15: el editor ES el diálogo,
  // igual que los de Recompensas/Beneficios/Ofertas/Plantillas. Antes lo
  // envolvía un `Modal` en su forma histórica desde la pantalla, y el editor
  // dibujaba a mano su encabezado (con `padding` y `border-bottom` propios,
  // que se sumaban al `padding: 24` del panel: 46px arriba) y su fila de
  // botones dentro de la columna izquierda. Quien abre el diálogo sigue
  // decidiendo el ancho.
  modal: EditorModalTarget;
}

function PromotionEditor({
  promotion,
  canWrite,
  submitting,
  onSubmit,
  onCancel,
  modal,
}: PromotionEditorProps) {
  const offers = useOffers();
  const { register, handleSubmit, watch, setValue, errors, setError } =
    useEditorForm<PromotionFormValues>(defaultsFor(promotion));
  const values = watch();

  const submit = (formValues: PromotionFormValues) => {
    const parsed = promotionFormSchema.safeParse(formValues);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        setError(issue.path.join(".") as Path<PromotionFormValues>, {
          message: issue.message,
        });
      }
      return;
    }
    onSubmit(buildInput(parsed.data), (error) =>
      applyApiFieldErrors(
        error,
        (field, fieldError) =>
          setError(field as Path<PromotionFormValues>, fieldError),
        PROMOTION_FORM_FIELDS,
      ),
    );
  };

  return (
    // El `<form>` lo pone `EditorModal` (con `display: contents`) alrededor
    // del `Modal` entero, así que "Guardar y publicar" (`type="submit"`) sigue
    // siendo descendiente del formulario desde el pie FIJO. El `padding` y el
    // divisor de cada región los pone el `Modal`: los 22/24/26 que este
    // editor dibujaba a mano se borran —venían sumándose al `padding: 24`
    // del panel, el mismo defecto de doble padding del punto #15.
    <EditorModal
      modal={modal}
      onSubmit={handleSubmit(submit)}
      header={
        // SheetHead (admin.jsx:301-306): eyebrow + título + Toggle de estado.
        // Punto #21 (revisión en vivo 2026-09-22): eyebrow + título salen de
        // `DialogHeader` como en todos los diálogos (h3 de 23px en vez del h2
        // de 25px del prototipo); el Toggle sigue debajo, como antes.
        <>
          <DialogHeader
            eyebrow={promotion ? "Editar promoción" : "Nueva promoción"}
            title={values.titleEs || "Promoción sin título"}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginTop: 9,
            }}
          >
            <Toggle
              on={values.active}
              onChange={(next) =>
                setValue("active", next, { shouldDirty: true })
              }
              ariaLabel="Activa"
            />
            <span
              style={{
                font: `500 12px ${tokens.font.ui}`,
                color: values.active
                  ? tokens.text.secondary
                  : tokens.text.disabled,
              }}
            >
              {values.active
                ? "Activa en el carrusel"
                : "Inactiva · no se muestra"}
            </span>
          </div>
        </>
      }
      footer={
        // El pie estaba dentro de la columna izquierda, pegado abajo del
        // último campo y alineado a la IZQUIERDA con "Guardar" primero. Pasa
        // a `DialogActions`, la misma fila del resto de los diálogos
        // (Cancelar a la izquierda, confirmación a la derecha), y los botones
        // crudos `.btn btn-primary`/`.btn-link` pasan a `PortalButton`.
        <DialogActions onCancel={onCancel}>
          <Can module="content" action={promotion ? "canEdit" : "canCreate"}>
            <PortalButton type="submit" size="small" disabled={submitting}>
              {submitting ? "Guardando…" : "Guardar y publicar"}
            </PortalButton>
          </Can>
        </DialogActions>
      }
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(360px,1fr) 402px",
          gap: 24,
          alignItems: "start",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <fieldset
            disabled={!canWrite}
            style={{ border: "none", margin: 0, padding: 0 }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "0 18px",
              }}
            >
              <BilingualField
                fieldKey="overline"
                label="Etiqueta"
                registerEs={register("overlineEs")}
                registerEn={register("overlineEn")}
                placeholderEs="Promoción del mes"
              />
              <BilingualField
                fieldKey="cta"
                label="Texto del botón"
                registerEs={register("ctaEs")}
                registerEn={register("ctaEn")}
              />
            </div>
            <BilingualField
              fieldKey="title"
              label="Título"
              required
              registerEs={register("titleEs")}
              registerEn={register("titleEn")}
              error={errors.titleEs?.message}
            />
            <BilingualField
              fieldKey="subtitle"
              label="Bajada"
              multiline
              registerEs={register("subtitleEs")}
              registerEn={register("subtitleEn")}
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "0 18px",
              }}
            >
              <LabeledField label="Vigente desde">
                <input
                  type="date"
                  {...register("validFrom")}
                  style={inputStyle}
                />
              </LabeledField>
              <LabeledField
                label="Vigente hasta"
                hint="Vacío = vigencia indefinida."
                error={errors.validUntil?.message}
              >
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="date"
                    {...register("validUntil")}
                    style={inputStyle}
                  />
                  {values.validUntil ? (
                    <button
                      type="button"
                      className="chip"
                      style={{ flex: "none" }}
                      onClick={() =>
                        setValue("validUntil", "", { shouldDirty: true })
                      }
                    >
                      Indefinida
                    </button>
                  ) : null}
                </div>
              </LabeledField>
            </div>
            <LabeledField
              label="Imagen"
              required
              hint="Recomendado 1600×900 px · JPG o PNG."
              error={errors.imageUrl?.message}
            >
              <ImageUploadBox>
                <ImagePreviewBox imageUrl={values.imageUrl} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      font: `400 11px/1.45 ${tokens.font.ui}`,
                      color: tokens.text.disabled,
                      margin: "0 0 8px",
                    }}
                  >
                    Se muestra a ancho completo en el carrusel de inicio.
                  </p>
                  <FileUploadChip
                    entityType="promotion"
                    label="Reemplazar"
                    ariaLabel="Subir imagen de la promoción"
                    onUploaded={(image) => {
                      // PLAN-007 Fase 4d: `imageUrl` solo para la
                      // previsualización inmediata (vigente 15 minutos); el
                      // `fileId` es lo que de verdad viaja al servidor.
                      setValue("imageUrl", image.imageUrl, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      setValue("imageFileId", image.fileId, {
                        shouldDirty: true,
                      });
                    }}
                  />
                </div>
              </ImageUploadBox>
            </LabeledField>
            <LabeledField label="Oferta enlazada">
              {/* QA-MTZ-002 ronda 3 (#1): las ofertas cargan aparte; un
                  <select> no controlado montado antes de que llegaran caía a
                  «Sin oferta enlazada» y no volvía a aplicar el valor. Con
                  `value` el navegador lo reaplica cuando aparecen las
                  opciones. */}
              <select
                {...register("offerId")}
                value={values.offerId ?? ""}
                style={selectStyle}
              >
                <option value="">Sin oferta enlazada</option>
                {offers.isLoading ? (
                  <option value="" disabled>
                    Cargando…
                  </option>
                ) : (
                  offers.data?.map((offer) => (
                    <option key={offer.id} value={offer.id}>
                      {text(offer.title, "es")}
                      {offer.active ? "" : " (inactiva)"}
                    </option>
                  ))
                )}
              </select>
            </LabeledField>
          </fieldset>
        </div>

        {/* PA-019-C: previsualización, visible solo en el editor, reflejando
          el formulario en edición, incluso sin guardar (SPEC-206 §7). */}
        {/* Revisión en vivo 2026-09-22, fase 4d: `STICKY_COLUMN_TOP` mide el
            alto de `StickyScreenHeader`, la cabecera de la PÁGINA —que no
            existe dentro de un diálogo—, así que acá empujaba la
            previsualización ~87px hacia abajo sin nada que la tapara. Dentro
            del cuerpo del `Modal` el contenedor de scroll es el cuerpo, y el
            colchón correcto es cero: el `padding` de la región ya lo pone. */}
        <div style={{ position: "sticky", top: 0 }}>
          <div
            className="label"
            id="promotion-preview-label"
            style={{ marginBottom: 12 }}
          >
            Vista previa · pantalla de inicio
          </div>
          {/* Accesibilidad por teclado (axe `scrollable-region-focusable`):
            un contenedor con `overflow-y: auto` y contenido que lo excede
            necesita foco propio para que quien no usa mouse pueda
            desplazarlo. `welcome-content` ya lo tenía; acá faltaba —
            hallazgo de la revisión en vivo del 2026-09-22 (punto #13),
            portado desde WelcomeContentScreen. El `role="region"` se
            etiqueta con el rótulo visible de arriba
            (`aria-labelledby`), sin duplicar texto. No cambia el look. */}
          <div
            className="scr-scroll"
            data-testid="promotion-preview-frame"
            role="region"
            aria-labelledby="promotion-preview-label"
            tabIndex={0}
            style={{
              width: 402,
              minHeight: 420,
              maxHeight: 500,
              borderRadius: 24,
              border: `1px solid ${tokens.surface.line2}`,
            }}
          >
            <PromotionPreview
              eyebrow={values.overlineEs}
              title={values.titleEs}
              subtitle={values.subtitleEs}
              cta={values.ctaEs}
              imageUrl={values.imageUrl}
              until={previewUntilOf(values)}
            />
          </div>
          <PreviewScrollHint />
          <div
            style={{
              font: `400 11px/1.5 ${tokens.font.ui}`,
              color: tokens.text.disabled,
              marginTop: 10,
            }}
          >
            La vista previa refleja los cambios sin publicar.
          </div>
        </div>
      </div>
    </EditorModal>
  );
}

// Revisión en vivo 2026-09-22, puntos #3/#4A/#15: acá vivía
// `InactivateConfirmDialog`, un diálogo de confirmación propio —armado sobre
// la forma histórica del `Modal`, con `.btn-link`/`.btn btn-sm` y el color de
// peligro repetido a mano— que hacía exactamente lo que ya hace
// `ImpactConfirmDialog`, el diálogo de confirmación COMPARTIDO (el mismo
// movimiento que el punto #15 pidió para "¿Inactivar a «…»?" de Círculo de
// miembros). Se borra y su llamador pasa al componente compartido: tres
// regiones fijas, `PortalButton` con `tone="danger"` y el texto literal de
// PA-019-F intacto en la caja de impacto. Lo único que se pierde es el
// círculo con "!" que este diálogo dibujaba a mano y que ningún otro diálogo
// del portal tiene.

const COLS =
  "56px minmax(112px,1fr) minmax(190px,1.9fr) minmax(146px,1.1fr) 104px 220px";

export function HomeContentScreen() {
  const toast = useToast();
  const { hasPermission } = useSession();
  const promotions = usePromotions();
  const mutations = usePromotionMutations();
  const [editing, setEditing] = useState<Promotion | null | undefined>(
    undefined,
  );
  const [pendingInactivate, setPendingInactivate] = useState<Promotion | null>(
    null,
  );

  const canCreate = hasPermission("content", "canCreate");
  const canEdit = hasPermission("content", "canEdit");
  const canInactivate = hasPermission("content", "canInactivate");

  const items = promotions.data?.items ?? [];
  const submitting = mutations.create.isPending || mutations.edit.isPending;

  const onSaved = (promotion: Promotion) => {
    setEditing(undefined);
    toast.show(`${text(promotion.title, "es")} guardada y publicada`);
    for (const warning of promotion.warnings ?? [])
      toast.show(warning.message, "neutral");
  };

  // T2 (qa-bug-matrix-2026-09.md §4.1): `if (message)` descartaba en
  // silencio cualquier código sin mapear — resolutor compartido.
  const onSubmit = (
    input: PromotionInput,
    applyFieldErrors: (error: unknown) => boolean,
  ) => {
    const handleError = (error: unknown) =>
      reportUnlessFieldError(
        toast,
        error,
        applyFieldErrors,
        SERVER_ERROR_MESSAGES,
      );
    if (editing?.id) {
      mutations.edit.mutate(
        { id: editing.id, input },
        { onSuccess: onSaved, onError: handleError },
      );
      return;
    }
    mutations.create.mutate(input, {
      onSuccess: onSaved,
      onError: handleError,
    });
  };

  const confirmInactivate = () => {
    if (!pendingInactivate) return;
    mutations.inactivate.mutate(pendingInactivate.id, {
      onSuccess: () => {
        toast.show(`${text(pendingInactivate.title, "es")} inactivada`);
        setPendingInactivate(null);
      },
      onError: (error) => reportError(toast, error, SERVER_ERROR_MESSAGES),
    });
  };

  // api-http.md (comentario de PromotionController): reactivar es una edición
  // que mueve `active` a `true`, no un verbo propio — mismos helpers que el
  // editor (defaultsFor + buildInput), sin repetir la forma de `PromotionInput`.
  const reactivate = (promotion: Promotion) => {
    const title = text(promotion.title, "es");
    const input = { ...buildInput(defaultsFor(promotion)), active: true };
    mutations.edit.mutate(
      { id: promotion.id, input },
      {
        onSuccess: (updated) => {
          toast.show(`${text(updated.title, "es")} activada`);
          for (const warning of updated.warnings ?? [])
            toast.show(warning.message, "neutral");
        },
        // T2 (qa-bug-matrix-2026-09.md §4.4, #30/#35): sin `onError` el
        // conmutador volvía solo a su posición sin decir nada.
        onError: (error) =>
          reportErrorWithPrefix(toast, `No se pudo reactivar ${title}.`, error),
      },
    );
  };

  const toggleActive = (promotion: Promotion) => {
    if (promotion.active) {
      setPendingInactivate(promotion);
      return;
    }
    reactivate(promotion);
  };

  // T2 (qa-bug-matrix-2026-09.md §5.5, #19): `mutations.move.mutate` iba sin
  // `onSuccess` ni `onError` — reordenar no confirmaba nada, funcionara o no.
  const moveUp = (id: string) =>
    mutations.move.mutate(
      { id, direction: "up" },
      {
        onSuccess: () => toast.show("Orden actualizado."),
        onError: (error) => reportError(toast, error),
      },
    );
  const moveDown = (id: string) =>
    mutations.move.mutate(
      { id, direction: "down" },
      {
        onSuccess: () => toast.show("Orden actualizado."),
        onError: (error) => reportError(toast, error),
      },
    );

  const activeCount = items.filter((p) => p.active).length;
  const listWarnings = promotions.data?.warnings ?? [];

  return (
    <div>
      {/* Cabecera de pantalla, patrón de MembersScreen.tsx:314-317 — entrada
        de primer nivel de NAV, sin pestañas de grupo. Título literal del
        prototipo (`NAV.find(n => n.k === tab).label`, admin.jsx:3830). La
        bajada cambia «eliminar» por «inactivar» contra admin.jsx:3836: la
        resolución humana PA-019-F (corrida
        2026-09-14-spec-206-home-screen-promotions-r2) retiró el borrado
        permanente, y SPEC-206 §9 solo permite ver, crear, editar e
        inactivar.

        Volvió a ser un `<header>` plano en flujo normal: el humano revirtió
        STK-01 el 2026-09-22 (revisión en vivo, misma sesión) — vivió un
        tiempo corto como `StickyScreenHeader` (extraída en el punto #9,
        ajustada en las fases 3a/4b), pero el pedido final fue que esta
        pantalla se comporte como las otras ocho del portal, con la cabecera
        en el flujo, no fija. `StickyScreenHeader` se eliminó (sin más
        consumidores). */}
      <header style={{ marginBottom: 0 }}>
        <h1 style={{ margin: 0, font: `300 34px ${tokens.font.display}` }}>
          Pantalla de inicio
        </h1>
        <p
          style={{
            color: tokens.text.tertiary,
            margin: "2px 0 26px",
            fontSize: 13,
          }}
        >
          Promociones del carrusel de inicio: crear, editar, activar o
          inactivar.
        </p>
      </header>
      <div
        style={{
          border: `1px solid ${tokens.surface.line}`,
          borderRadius: 18,
          background: tokens.surface.bg2,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 20,
            padding: "20px 24px 16px",
          }}
        >
          <div>
            {/* `h2`, no `h3`: corrección de una regresión propia de esta
              corrida (heading-order de axe), no deuda heredada. El `<h1>`
              de la cabecera de pantalla es nuevo en `home-content` (arriba);
              antes de agregarlo, este `<h3>` no violaba nada porque no
              había `h1` previo. El tamaño visual (22px) queda fijado
              inline, así que retagear a `h2` no mueve la baseline: no hay
              selector de etiqueta `h2`/`h3` en base.css que aplique tamaño
              por defecto. */}
            <h2 className="display" style={{ fontSize: 22, margin: 0 }}>
              Promociones del carrusel
            </h2>
            <div
              style={{
                font: `400 12px ${tokens.font.ui}`,
                color: tokens.text.tertiary,
                marginTop: 5,
              }}
            >
              Las promociones activas se muestran en la pantalla de inicio de
              todos los miembros, en este orden.
            </div>
            {listWarnings.length > 0 ? (
              <div style={{ marginTop: 10, maxWidth: 460 }}>
                {listWarnings.map((warning, index) => (
                  <InlineNote key={index}>{warning.message}</InlineNote>
                ))}
              </div>
            ) : null}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flex: "none",
            }}
          >
            <span
              style={{
                font: `500 11.5px ${tokens.font.ui}`,
                color: tokens.text.tertiary,
                whiteSpace: "nowrap",
              }}
            >
              {activeCount} activas · {items.length} en total
            </span>
            <Can module="content" action="canCreate">
              <PortalButton size="small" onClick={() => setEditing(null)}>
                <Icon name="plus" size={15} />{" "}
                <span style={{ whiteSpace: "nowrap" }}>Nueva promoción</span>
              </PortalButton>
            </Can>
          </div>
        </div>

        {promotions.isLoading ? (
          <div style={{ padding: 24, color: tokens.text.tertiary }}>
            Cargando…
          </div>
        ) : items.length === 0 ? (
          <div
            style={{
              padding: "62px 24px 72px",
              textAlign: "center",
              borderTop: `1px solid ${tokens.surface.line}`,
            }}
          >
            <h3 className="display" style={{ fontSize: 21, margin: "0 0 8px" }}>
              Sin promociones
            </h3>
            <div
              style={{
                font: `400 13px/1.6 ${tokens.font.ui}`,
                color: tokens.text.tertiary,
                maxWidth: 400,
                margin: "0 auto 20px",
              }}
            >
              El carrusel de inicio queda oculto hasta que exista al menos una
              promoción activa.
            </div>
            <Can module="content" action="canCreate">
              <PortalButton size="small" onClick={() => setEditing(null)}>
                <Icon name="plus" size={15} /> Crear la primera
              </PortalButton>
            </Can>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            {/* TBL-01 (evidence/human-resolution-tbl-01.json): divergencia
              deliberada contra el prototipo. `width: "max-content"`
              (admin.jsx:259) resuelve las columnas `minmax(X, Nfr)` de COLS a
              su ancho intrínseco y deja un hueco muerto a la derecha de la
              tarjeta — el prototipo tiene el mismo defecto visual, pero el
              humano pidió que la tabla ocupe el 100% del ancho disponible
              acá. `width: "100%"` deja que las columnas fr se repartan el
              ancho de la tarjeta; `overflowX: auto` del contenedor padre
              sigue permitiendo scroll horizontal cuando el viewport es más
              angosto que `minWidth`. */}
            <div style={{ minWidth: 940, width: "100%" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: COLS,
                  gap: 14,
                  alignItems: "center",
                  padding: "0 24px 11px",
                  borderBottom: `1px solid ${tokens.surface.line}`,
                }}
              >
                {["", "Etiqueta", "Título", "Vigencia", "Estado"].map((h) => (
                  <span key={h} className="label">
                    {h}
                  </span>
                ))}
                <span className="label" style={{ textAlign: "right" }}>
                  Acciones
                </span>
              </div>

              {items.map((promotion, index) => {
                const updated = shortDateTime(promotion.updatedAt);
                return (
                  <div
                    key={promotion.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: COLS,
                      gap: 14,
                      alignItems: "center",
                      padding: "13px 24px",
                      borderBottom: `1px solid ${tokens.surface.line}`,
                      opacity: promotion.active ? 1 : 0.5,
                    }}
                  >
                    <ImageWithPlaceholder
                      src={promotion.imageUrl}
                      iconSize={14}
                      showText={false}
                      imageStyle={{
                        width: 56,
                        height: 36,
                        objectFit: "cover",
                        borderRadius: 8,
                        border: `1px solid ${tokens.surface.line}`,
                      }}
                    />
                    <span
                      style={{
                        font: `600 12px ${tokens.font.ui}`,
                        color: tokens.text.primary,
                        letterSpacing: "0.02em",
                      }}
                    >
                      {text(promotion.overline ?? null, "es")}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          font: `600 12.5px ${tokens.font.ui}`,
                          color: tokens.text.primary,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {text(promotion.title, "es")}
                      </div>
                      <div
                        style={{
                          font: `400 10.5px ${tokens.font.ui}`,
                          color: tokens.text.disabled,
                          marginTop: 2,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {text(promotion.cta ?? null, "es")}
                      </div>
                      {/* PA-019-D: usuario y fecha de la última actualización,
                        no editable (SPEC-206 §8). */}
                      {updated ? (
                        <div
                          style={{
                            font: `400 10px ${tokens.font.ui}`,
                            color: tokens.text.disabled,
                            marginTop: 2,
                          }}
                        >
                          Actualizado {updated}
                          {promotion.updatedBy
                            ? ` · ${promotion.updatedBy}`
                            : ""}
                        </div>
                      ) : null}
                      {promotion.offer && !promotion.offer.valid ? (
                        <div
                          style={{
                            font: `500 10px ${tokens.font.ui}`,
                            color: tokens.danger.base,
                            marginTop: 2,
                          }}
                        >
                          La oferta enlazada venció; la promoción dejó de ser
                          interactiva
                        </div>
                      ) : null}
                    </div>
                    <span
                      style={{
                        font: "400 12.5px var(--ui)",
                        color: tokens.text.secondary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatValidity(promotion)}
                    </span>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 9 }}
                    >
                      <Toggle
                        on={promotion.active}
                        onChange={() => toggleActive(promotion)}
                        disabled={!canInactivate}
                        ariaLabel="Activa"
                      />
                      <span
                        style={{
                          font: `500 11px ${tokens.font.ui}`,
                          color: promotion.active
                            ? tokens.text.secondary
                            : tokens.text.disabled,
                        }}
                      >
                        {promotion.active ? "Activa" : "Inactiva"}
                      </span>
                    </div>
                    {/* Revisión en vivo 2026-09-22, punto #10: este era el
                      grupo que motivó el punto — cuatro chips de TEXTO en una
                      columna de 220px, con `flexWrap: "wrap"` para que
                      "Inactivar" (el más ancho) cayera solo a una segunda
                      fila. Con ícono + tooltip el grupo mide 4×40 + 3×6 =
                      178px y entra en la columna sin wrap, así que el
                      `flexWrap` deja de tener sentido y se quita: si un día
                      vuelve a no entrar, tiene que verse, no acomodarse
                      solo. El tono de peligro de "Inactivar" se conserva: es
                      la única acción de fila del portal que lo lleva. */}
                    <div
                      style={{
                        display: "flex",
                        gap: tableSpacing.actionGap,
                        justifyContent: "flex-end",
                      }}
                    >
                      <RowIconAction
                        label="Subir"
                        icon="chevdown"
                        rotate={180}
                        disabled={index === 0}
                        onClick={() => moveUp(promotion.id)}
                      />
                      <RowIconAction
                        label="Bajar"
                        icon="chevdown"
                        disabled={index === items.length - 1}
                        onClick={() => moveDown(promotion.id)}
                      />
                      {canEdit ? (
                        <RowIconAction
                          label="Editar"
                          icon="edit"
                          onClick={() => setEditing(promotion)}
                        />
                      ) : null}
                      {canInactivate && promotion.active ? (
                        <RowIconAction
                          label="Inactivar"
                          icon="deactivate"
                          tone="danger"
                          onClick={() => setPendingInactivate(promotion)}
                        />
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {editing !== undefined ? (
        // El `Modal` ya no se arma acá para envolver al editor: lo abre el
        // propio `PromotionEditor` con `EditorModal`, igual que los editores
        // de Recompensas, Beneficios, Ofertas y Plantillas. La pantalla sigue
        // decidiendo el ancho, que es lo único suyo que había en ese `Modal`.
        <PromotionEditor
          key={editing?.id ?? "new"}
          promotion={editing}
          canWrite={editing?.id ? canEdit : canCreate}
          submitting={submitting}
          modal={{
            label: editing?.id ? "Editar promoción" : "Nueva promoción",
            width: 1020,
            onClose: () => setEditing(undefined),
          }}
          onSubmit={onSubmit}
          onCancel={() => setEditing(undefined)}
        />
      ) : null}

      <ImpactConfirmDialog
        open={pendingInactivate !== null}
        eyebrow="Pantalla de inicio"
        title="¿Inactivar la promoción?"
        message={`«${pendingInactivate ? text(pendingInactivate.title, "es") : ""}» se inactiva y deja de mostrarse en el carrusel de inicio. Puede reactivarla cuando quiera.`}
        onCancel={() => setPendingInactivate(null)}
        onConfirm={confirmInactivate}
        confirmLabel="Sí, inactivar"
        busy={mutations.inactivate.isPending}
      />
    </div>
  );
}
